import React, { useState, useEffect } from 'react';
import { UsersIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface HerdComposition {
  category: string;
  count: number;
  percentage: number;
}

interface AgeGroup {
  age_range: string;
  count: number;
  percentage: number;
}

interface BreedDistribution {
  breed: string;
  count: number;
  percentage: number;
}

interface StatusBreakdown {
  status: string;
  count: number;
  percentage: number;
}

interface ShedUtilization {
  shed_id: string;
  shed_name: string;
  capacity?: number;
  current_count: number;
  utilization_percentage: number;
}

interface PopulationTrend {
  month: string;
  total_count: number;
  does: number;
  bucks: number;
  kids: number;
}

interface GoatPopulationReport {
  location_id: string;
  location_name: string;
  report_date: string;
  total_herd_size: number;
  composition: HerdComposition[];
  age_distribution: AgeGroup[];
  breed_distribution: BreedDistribution[];
  status_breakdown: StatusBreakdown[];
  doe_buck_ratio: number;
  kid_percentage: number;
  average_age_months?: number;
  shed_utilization: ShedUtilization[];
  population_trends: PopulationTrend[];
}

const GoatPopulationReportPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatPopulationReport | null>(null);

  useEffect(() => {
    if (selectedLocation?.id) {
      fetchReport();
    }
  }, [selectedLocation?.id]);

  const fetchReport = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/population', {
        params: {
          location_id: selectedLocation.id,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load population report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container-mobile py-6 text-center">Loading...</div>;
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="text-center">
        <UsersIcon className="h-16 w-16 text-indigo-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Population & Herd Management Report</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-gray-900">{report.total_herd_size}</div>
              <div className="text-sm text-gray-600">Total Herd Size</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-blue-600">{report.doe_buck_ratio.toFixed(1)}:1</div>
              <div className="text-sm text-gray-600">Doe:Buck Ratio</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-green-600">{report.kid_percentage.toFixed(1)}%</div>
              <div className="text-sm text-gray-600">Kid Percentage</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-purple-600">
                {report.average_age_months ? report.average_age_months.toFixed(1) : 'N/A'}
              </div>
              <div className="text-sm text-gray-600">Avg Age (months)</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Herd Composition</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {report.composition.map((comp, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-lg font-semibold text-gray-900 capitalize">{comp.category}</div>
                  <div className="text-2xl font-bold text-blue-600">{comp.count}</div>
                  <div className="text-sm text-gray-600">{comp.percentage.toFixed(1)}% of herd</div>
                </div>
              ))}
            </div>
          </div>

          {report.shed_utilization.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Shed Utilization</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shed</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilization</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.shed_utilization.map((shed, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{shed.shed_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{shed.capacity ?? 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{shed.current_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{shed.utilization_percentage.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.population_trends.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Population Trends (Last 12 Months)</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Does</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bucks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kids</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.population_trends.map((trend, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{trend.month}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{trend.total_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{trend.does}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{trend.bucks}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{trend.kids}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoatPopulationReportPage;

